import { Entity } from "@common/types/entity";
import {
    ConsolidationGroupResponse,
    ConsolidationRelationshipResponse,
} from "@common/schemas/domains/accounting/consolidation.schema";
import { ConsolidationEntityRelationshipType } from "@common/types/domains/accounting/consolidation";
import { isActiveRelationship } from "./ownershipGraph.helpers";
import {
    getActiveOwnershipRelationships,
    getConflictingOwnershipRelationships,
    isOwnershipDescendant,
} from "./ownershipProposalGraph.helpers";
import {
    OWNERSHIP_ACTIVE_GROUP_MESSAGE,
    isConflictingRelationshipBackingActiveGroup,
    isOwnershipCreateLockedByConsolidation,
} from "./ownershipProposalGroupLock.helpers";

export {
    OWNERSHIP_ACTIVE_GROUP_MESSAGE,
    getOwnershipRelationshipDeleteBlockMessage,
} from "./ownershipProposalGroupLock.helpers";

export const OWNERSHIP_UPDATE_TITLE = "Update ownership?";
export const OWNERSHIP_INDIRECT_CYCLE_MESSAGE =
    "A subsidiary can't own its parent.";

export function getOwnershipUpdateConfirmationBody({
    sourceName,
    targetName,
}: {
    sourceName: string;
    targetName: string;
}): string {
    return `${sourceName} will become the 100% owner of ${targetName}. Existing ownership relationships will be removed.`;
}

export function getMultiTierConsolidationBlockedMessage(
    entityName: string,
): string {
    return `${entityName} can't be consolidated. Multi-tier consolidations aren't supported yet.`;
}

export function isOwnedByBusinessEntity({
    entityUuid,
    relationships,
    entitiesByUuid,
}: {
    entityUuid: string;
    relationships: ConsolidationRelationshipResponse[];
    entitiesByUuid: Map<string, Entity>;
}): boolean {
    return relationships.some(
        (relationship) =>
            isActiveRelationship(relationship) &&
            relationship.type ===
                ConsolidationEntityRelationshipType.ownership &&
            relationship.targetEntityUuid === entityUuid &&
            entitiesByUuid.get(relationship.sourceEntityUuid)?.isBusiness ===
                true,
    );
}

type OwnershipProposalClassification =
    | { kind: "duplicate" }
    | { kind: "create" }
    | {
          kind: "confirm";
          conflictingRelationshipIds: string[];
          confirmationBody: string;
      }
    | { kind: "blocked"; message: string };

interface ClassifyOwnershipProposalInput {
    sourceEntityUuid: string;
    targetEntityUuid: string;
    relationships: ConsolidationRelationshipResponse[];
    groups: ConsolidationGroupResponse[];
    sourceName: string;
    targetName: string;
}

export function classifyOwnershipProposal({
    sourceEntityUuid,
    targetEntityUuid,
    relationships,
    groups,
    sourceName,
    targetName,
}: ClassifyOwnershipProposalInput): OwnershipProposalClassification {
    const activeOwnershipRelationships =
        getActiveOwnershipRelationships(relationships);

    if (
        activeOwnershipRelationships.some(
            (relationship) =>
                relationship.sourceEntityUuid === sourceEntityUuid &&
                relationship.targetEntityUuid === targetEntityUuid,
        )
    ) {
        return { kind: "duplicate" };
    }

    if (
        isOwnershipCreateLockedByConsolidation({
            sourceEntityUuid,
            targetEntityUuid,
            groups,
        })
    ) {
        return {
            kind: "blocked",
            message: OWNERSHIP_ACTIVE_GROUP_MESSAGE,
        };
    }

    const conflictingRelationships = getConflictingOwnershipRelationships({
        sourceEntityUuid,
        targetEntityUuid,
        relationships,
    });
    const excludedRelationshipIds = new Set(
        conflictingRelationships.map((relationship) => relationship.id),
    );

    if (
        isOwnershipDescendant({
            ancestorUuid: targetEntityUuid,
            descendantUuid: sourceEntityUuid,
            relationships,
            excludedRelationshipIds,
        })
    ) {
        return {
            kind: "blocked",
            message: OWNERSHIP_INDIRECT_CYCLE_MESSAGE,
        };
    }

    if (
        isConflictingRelationshipBackingActiveGroup({
            relationships: conflictingRelationships,
            groups,
        })
    ) {
        return {
            kind: "blocked",
            message: OWNERSHIP_ACTIVE_GROUP_MESSAGE,
        };
    }

    if (conflictingRelationships.length === 0) {
        return { kind: "create" };
    }

    return {
        kind: "confirm",
        conflictingRelationshipIds: conflictingRelationships.map(
            (relationship) => relationship.id,
        ),
        confirmationBody: getOwnershipUpdateConfirmationBody({
            sourceName,
            targetName,
        }),
    };
}
